import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Chip,
  Button,
  Avatar,
  Grid,
} from '@mui/material';
import { Person, AccessTime } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Caretaker } from '../services/caretakerSearchService';
import { availabilityService, AvailabilitySummary } from '../services/availabilityService';
import RatingDisplay from './RatingDisplay';
import CaregiverBadgeRow from './CaregiverBadgeRow';
import FavoriteButton from './FavoriteButton';

interface CaretakerCardProps {
  caretaker: Caretaker;
  onViewProfile?: (caretaker: Caretaker) => void;
}

const CaretakerCard: React.FC<CaretakerCardProps> = ({
  caretaker,
  onViewProfile
}) => {
  const navigate = useNavigate();
  const [availabilitySummary, setAvailabilitySummary] = useState<AvailabilitySummary | null>(null);

  useEffect(() => {
    let mounted = true;
    const authToken = localStorage.getItem('authToken');

    if (!authToken) {
      return undefined;
    }

    availabilityService
      .getSummary(caretaker.id)
      .then((summary) => {
        if (mounted) {
          setAvailabilitySummary(summary);
        }
      })
      .catch(() => {
        if (mounted) {
          setAvailabilitySummary(null);
        }
      });

    return () => {
      mounted = false;
    };
  }, [caretaker.id]);

  const handleViewProfile = () => {
    if (onViewProfile) {
      onViewProfile(caretaker);
    } else {
      navigate(`/caretaker/${caretaker.id}`);
    }
  };

  const getDisplayName = (): string => {
    const firstName = caretaker.firstName || '';
    const lastName = caretaker.lastName || '';
    return `${firstName} ${lastName}`.trim() || 'Unknown Caretaker';
  };

  const getPrimarySpecialty = (): string => {
    return caretaker.specialties.length > 0 
      ? caretaker.specialties[0].name 
      : 'Healthcare Provider';
  };

  const getAvailabilityText = (): string => {
    if (availabilitySummary) {
      if (availabilitySummary.sameDayAvailable) {
        return 'Same-day availability';
      }

      if (availabilitySummary.nextAvailableDate) {
        return `Next available ${new Date(availabilitySummary.nextAvailableDate).toLocaleDateString()}`;
      }

      if (availabilitySummary.isGenerallyAvailable) {
        return 'Generally available';
      }

      return 'No availability listed';
    }

    if (caretaker.availability.length === 0) {
      return 'Availability not set';
    }
    
    const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });
    const todayAvailability = caretaker.availability.find(
      avail => avail.dayOfWeek.toLowerCase() === today.toLowerCase()
    );
    
    if (todayAvailability) {
      return `Available today ${todayAvailability.startTime}-${todayAvailability.endTime}`;
    }
    
    return `Available ${caretaker.availability.length} days/week`;
  };

  const availabilitySignal = useMemo(() => {
    if (!availabilitySummary) {
      return null;
    }

    if (availabilitySummary.sameDayAvailable) {
      return {
        label: 'Same-day',
        color: 'success' as const,
      };
    }

    if (availabilitySummary.isGenerallyAvailable) {
      return {
        label: 'Generally available',
        color: 'primary' as const,
      };
    }

    return {
      label: 'Currently unavailable',
      color: 'default' as const,
    };
  }, [availabilitySummary]);

  const getAvatarUrl = (): string => {
    if (caretaker.avatar) {
      return caretaker.avatar;
    }
    // Return a default avatar or placeholder
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(getDisplayName())}&background=random&color=fff&size=200`;
  };

  return (
    <Card
      sx={{
        position: 'relative',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 3,
        },
      }}
    >
      {/* FEAT-057 — Save / Favorite Caregivers (MVP-18.29). Heart toggle in the
          top-right of the discovery card; auth-gated via FavoriteButton. */}
      <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
        <FavoriteButton
          caregiverId={caretaker.id}
          size="small"
          summary={{
            id: caretaker.id,
            firstName: caretaker.firstName,
            lastName: caretaker.lastName,
            avatar: caretaker.avatar,
          }}
        />
      </Box>

      {/* Avatar */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          pt: 2,
          pb: 1,
        }}
      >
        <Avatar
          src={getAvatarUrl()}
          alt={getDisplayName()}
          sx={{
            width: 80,
            height: 80,
            border: '3px solid',
            borderColor: 'primary.main',
          }}
        />
      </Box>

      <CardContent sx={{ flexGrow: 1, pt: 1 }}>
        {/* Name and Primary Specialty */}
        <Box textAlign="center" mb={2}>
          <Typography variant="h6" component="h3" gutterBottom>
            {getDisplayName()}
          </Typography>
          <Chip
            label={getPrimarySpecialty()}
            color="primary"
            size="small"
            variant="outlined"
          />
        </Box>

        {/* Rating */}
        <Box display="flex" justifyContent="center" mb={2}>
          <RatingDisplay
            rating={caretaker.averageRating}
            reviewCount={caretaker.reviewCount}
            size="medium"
          />
        </Box>

        {/* FEAT-039 — Trust indicator badge row (MVP-8.1, 8.3, 8.37-8.40, 18.14).
            Badges render below the rating and above secondary specialties so the
            primary trust signals are visible from the discovery grid card. */}
        <Box display="flex" justifyContent="center" mb={2}>
          <CaregiverBadgeRow caregiverId={caretaker.id} size="small" density="compact" />
        </Box>

        {/* Specialties */}
        {caretaker.specialties.length > 1 && (
          <Box mb={2}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Specialties:
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={0.5}>
              {caretaker.specialties.slice(1, 3).map((specialty) => (
                <Chip
                  key={specialty.id}
                  label={specialty.name}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem' }}
                />
              ))}
              {caretaker.specialties.length > 3 && (
                <Chip
                  label={`+${caretaker.specialties.length - 3} more`}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem' }}
                />
              )}
            </Box>
          </Box>
        )}

        {/* Availability */}
        <Box display="flex" alignItems="center" mb={2}>
          <AccessTime sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
            {getAvailabilityText()}
          </Typography>
        </Box>
        {availabilitySignal && (
          <Chip
            label={availabilitySignal.label}
            color={availabilitySignal.color}
            size="small"
            sx={{ mb: 2 }}
            data-testid={`availability-signal-${caretaker.id}`}
          />
        )}

        {/* View Profile Button */}
        <Button
          variant="contained"
          fullWidth
          onClick={handleViewProfile}
          sx={{
            mt: 'auto',
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          View Profile
        </Button>
      </CardContent>
    </Card>
  );
};

export default CaretakerCard;
